<template>
  <svg
    class="icon"
    :width="computedWidth"
    :height="computedHeight"
    :style="iconStyle"
    viewBox="0 0 1024 1024"
    role="img"
    aria-hidden="true"
  >
    <use :href="symbolHref"></use>
  </svg>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  /**
   * props:
   * - size: 优先级最高。可以是 number (视为 px) 或 字符串（可带单位，如 '2em'、'24px'、'100%'）
   * - width / height: 单独指定宽高（优先级低于 size）
   * - color: 填充颜色（会传给 use 的 fill via CSS color）。传 "svg" 时继承父级颜色。
   * - prefix/name: 生成引用 href
   */

  const props = defineProps({
    prefix: { type: String, default: '#icon-' },
    name: { type: String, required: true },
    color: { type: String, default: '' },
    size: { type: [String, Number], default: undefined },
    width: { type: [String, Number], default: undefined },
    height: { type: [String, Number], default: undefined },
  });

  /** 辅助：把数值或字符串转成带单位的值（number -> px, string 保持） */
  function withUnit(v: string | number | undefined) {
    if (v === undefined || v === null || v === '') return undefined;
    if (typeof v === 'number' && !Number.isNaN(v)) return `${v}px`;
    // 如果是字符串，直接返回（假设用户会写单位或百分比或 em）
    return v;
  }

  const symbolHref = computed(() => {
    const name = String(props.name || '').trim();
    if (name.startsWith('#')) return name;
    return `${props.prefix}${name}`;
  });

  const iconStyle = computed(() => {
    if (!props.color || props.color === 'svg') return undefined;
    return { color: props.color };
  });

  // 计算宽高：优先 size，其次 width/height，最终 fallback 为 '1em'（随字体伸缩）
  const computedWidth = computed(() => {
    if (props.size !== undefined) return withUnit(props.size) ?? '1em';
    return withUnit(props.width) ?? '1em';
  });
  const computedHeight = computed(() => {
    if (props.size !== undefined) return withUnit(props.size) ?? '1em';
    return withUnit(props.height) ?? '1em';
  });
</script>

<style scoped>
  .icon {
    display: inline-block;
    vertical-align: middle;
    /* 让 <use> 的 fill 跟随 color */
    fill: currentColor;
    /* 允许被容器控制尺寸（如果使用 width:100%/height:100%） */
    max-width: 100%;
    max-height: 100%;
  }
</style>
